/**
 * ULD站点状态统计表格组件
 * 展示各个站点中ULD各个状态的数量，支持展开显示不同类型详情
 */

import React, { useState, useEffect } from 'react';
import {
    Table,
    Card,
    Spin,
    Alert,
    Tag,
    Space,
    Tooltip,
    Typography,
    Statistic,
    Select,
    Row,
    Col,
    Input,
} from 'antd';
import {
    EnvironmentOutlined,
    DownOutlined,
    RightOutlined,
    DatabaseOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    ExclamationCircleOutlined,
} from '@ant-design/icons';

import { uldService } from '../../../../api/enhancedApi';
import { useUldAirports } from '../../hooks/useUldAirports';
import { 
    ULD_CORE_STATUS_COLORS,
    ULD_CORE_STATUS_LABELS
} from '../constants/statisticsConstants';
import { ULD_TYPES, ULD_OWNERSHIP_TYPE_OPTIONS } from '../../UldManagement/constants/uldConstants';
import { 
    getOwnerOptions, 
    getHolderOptions, 
    getGhaOptions 
} from '../../../../api/uldApi';

const { Text, Title } = Typography;
const { Option } = Select;

const UldLocationStatTable = ({ refreshTrigger, onFiltersChange }) => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [error, setError] = useState(null);
    const [expandedRowKeys, setExpandedRowKeys] = useState([]);
    
    // 筛选条件状态
    const [uldIdentifier, setUldIdentifier] = useState('');
    const [debouncedUldIdentifier, setDebouncedUldIdentifier] = useState('');
    const [selectedAirports, setSelectedAirports] = useState([]);
    const [selectedUldTypes, setSelectedUldTypes] = useState([]);
    const [selectedGhas, setSelectedGhas] = useState([]);
    const [selectedOwnershipTypes, setSelectedOwnershipTypes] = useState([]);
    const [selectedOwners, setSelectedOwners] = useState([]);
    const [selectedHolders, setSelectedHolders] = useState([]);

    // 走 ULD 内部的 useUldAirports（/v1/airports），不依赖 schedule:read
    const { airportsList, loading: airportsLoading, error: airportsError } = useUldAirports();

    // 筛选选项数据状态
    const [filterOptions, setFilterOptions] = useState({
        ghas: [],
        owners: [],
        holders: [],
    });
    const [optionsLoading, setOptionsLoading] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedUldIdentifier(uldIdentifier);
        }, 400);
        return () => clearTimeout(timer);
    }, [uldIdentifier]);

    // 获取筛选选项数据
    const fetchFilterOptions = async () => {
        setOptionsLoading(true);
        try {
            const [ghasRes, ownersRes, holdersRes] = await Promise.all([
                getGhaOptions(),
                getOwnerOptions(),
                getHolderOptions(),
            ]);

            setFilterOptions({
                ghas: ghasRes.data || [],
                owners: ownersRes.data || [],
                holders: holdersRes.data || [],
            });
        } catch (error) {
            console.error("获取筛选选项失败:", error);
        } finally {
            setOptionsLoading(false);
        }
    };

    // 组件挂载时获取筛选选项
    useEffect(() => {
        fetchFilterOptions();
    }, []);

    // 处理机场数据
    const getAirportOptions = () => {
        if (!airportsList || airportsList.length === 0) {
            return [];
        }

        const filteredAirports = airportsList
            .filter(airport => airport && airport.iata_code && airport.is_active)
            .sort((a, b) => a.iata_code.localeCompare(b.iata_code));

        return filteredAirports.map((airport) => ({
            key: airport.iata_code,
            value: airport.iata_code,
            label: airport.iata_code
        }));
    };

    const airportOptions = getAirportOptions();

    // 获取统计数据
    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            // 构建筛选参数
            const filters = {};
            
            // ULD识别码筛选
            if (debouncedUldIdentifier && debouncedUldIdentifier.trim()) {
                filters.uld_identifiers = debouncedUldIdentifier.trim();
            }
            
            // 机场筛选
            if (selectedAirports && selectedAirports.length > 0) {
                filters.airport_iatas = selectedAirports.join(',');
            }
            
            // ULD类型筛选
            if (selectedUldTypes && selectedUldTypes.length > 0) {
                filters.uld_types = selectedUldTypes.join(',');
            }
            
            // 地面处理代理筛选
            if (selectedGhas && selectedGhas.length > 0) {
                filters.ghas = selectedGhas.join(',');
            }
            
            // 所有权类型筛选
            if (selectedOwnershipTypes && selectedOwnershipTypes.length > 0) {
                filters.ownership_types = selectedOwnershipTypes.join(',');
            }
            
            // 所有者筛选
            if (selectedOwners && selectedOwners.length > 0) {
                filters.owners = selectedOwners.join(',');
            }
            
            // 持有者筛选
            if (selectedHolders && selectedHolders.length > 0) {
                filters.holders = selectedHolders.join(',');
            }

            const response = await uldService.getUldStatistics(filters);
            const statisticsData = response.data;
            
            // 转换为表格数据格式
            const tableData = transformToTableData(statisticsData);
            setData(tableData);
        } catch (err) {
            console.error('获取ULD站点统计失败:', err);
            setError('获取统计数据失败，请稍后重试');
        } finally {
            setLoading(false);
        }
    };

    // 转换数据为表格格式
    const transformToTableData = (statisticsData) => {
        const { airport_details = [] } = statisticsData;
        
        // 计算总计数据
        let totalSummary = {
            totalCount: 0,
            inStock: 0,
            delivered: 0,
            inTransit: 0,
            outOfService: 0,
            retired: 0,
            lost: 0
        };
        
        const tableData = airport_details.map((airport, index) => {
            const typeStats = airport.type_statistics || {};
            const uldList = airport.uld_list || []; // 具体的ULD列表
            
            // 如果有ULD列表，从列表中计算V3核心状态统计
            let inStock = 0, inTransit = 0, outOfService = 0, retired = 0, delivered = 0, lost = 0;
            
            if (uldList && uldList.length > 0) {
                // 从ULD列表中统计各状态数量和外放数量
                uldList.forEach(uld => {
                    // 统计外放数量 - 使用后端返回的字段
                    if (uld.delivered_to_consignee) {
                        delivered++;
                    }
                    
                    // 核心状态统计 - 重要：在库状态要排除已外放的ULD
                    switch (uld.core_status) {
                        case 'IN_STOCK':
                            // 只有未外放的ULD才计入在库数量，确保统计的是实际可调配数量
                            if (!uld.delivered_to_consignee) {
                                inStock++;
                            }
                            break;
                        case 'IN_TRANSIT':
                            inTransit++;
                            break;
                        case 'OUT_OF_SERVICE':
                            outOfService++;
                            break;
                        case 'RETIRED':
                            retired++;
                            break;
                        case 'LOST':
                            lost++;
                            break;
                    }
                });
            } else {
                // 兼容性处理：如果没有ULD列表，尝试从现有统计中获取（可能为0）
                const coreStatusStats = airport.core_status_statistics || {};
                // 注意：这里的统计可能没有排除外放ULD，需要后端支持
                inStock = coreStatusStats.IN_STOCK || 0;
                inTransit = coreStatusStats.IN_TRANSIT || 0;
                outOfService = coreStatusStats.OUT_OF_SERVICE || 0;
                retired = coreStatusStats.RETIRED || 0;
                lost = coreStatusStats.LOST || 0;
            }
            
            const totalCount = airport.total_count || (inStock + inTransit + outOfService + retired + lost);

            // 处理子表格数据
            let children = [];
            
            if (uldList && uldList.length > 0) {
                // 如果有具体的ULD列表，显示具体的ULD
                children = uldList.map((uld, uldIndex) => {
            return {
                        key: `${airport.airport_iata}-uld-${uldIndex}`,
                        uldNumber: uld.uld_number || uld.number || `ULD-${uldIndex}`,
                        uldType: uld.type || uld.uld_type || 'N/A',
                        totalCount: 1,
                        typeCount: 1,
                        // V3核心状态 - 单个ULD只有一个状态，在库状态需排除外放ULD
                        inStock: (uld.core_status === 'IN_STOCK' && !uld.delivered_to_consignee) ? 1 : 0,
                        inTransit: uld.core_status === 'IN_TRANSIT' ? 1 : 0,
                        outOfService: uld.core_status === 'OUT_OF_SERVICE' ? 1 : 0,
                        retired: uld.core_status === 'RETIRED' ? 1 : 0,
                        lost: uld.core_status === 'LOST' ? 1 : 0,
                        delivered: uld.delivered_to_consignee ? 1 : 0, // 外放状态
                        isChild: true,
                    };
                });
            } else {
                // 兼容性处理：如果没有具体ULD列表，则按类型统计展示
                const typeStatusBreakdown = airport.type_status_breakdown || {};
                children = Object.entries(typeStats).map(([uldType, typeCount]) => {
                    const typeCoreStatus = typeStatusBreakdown[uldType]?.core_status || {};
                    
                    // 计算该类型下V3核心状态的数量
                    let typeInStock = typeCoreStatus.IN_STOCK || 0;
                    const typeInTransit = typeCoreStatus.IN_TRANSIT || 0;
                    const typeOutOfService = typeCoreStatus.OUT_OF_SERVICE || 0;
                    const typeRetired = typeCoreStatus.RETIRED || 0;
                    const typeLost = typeCoreStatus.LOST || 0;
                    
                    // 计算该类型下的外放数量和修正后的在库数量
                    let typeDelivered = 0;
                    if (uldList && uldList.length > 0) {
                        // 计算该类型下的外放数量
                        typeDelivered = uldList.filter(uld => 
                            uld.type === uldType && uld.delivered_to_consignee
                        ).length;
                        
                        // 重新计算该类型下真实的在库数量（排除外放ULD）
                        typeInStock = uldList.filter(uld => 
                            uld.type === uldType && 
                            uld.core_status === 'IN_STOCK' && 
                            !uld.delivered_to_consignee
                        ).length;
                    }

                    return {
                        key: `${airport.airport_iata}-type-${uldType}`,
                        uldNumber: `${uldType} 类型汇总`,
                        uldType,
                        typeCount,
                        totalCount: typeCount,
                        // V3核心状态 - 在库数量已排除外放ULD
                        inStock: typeInStock,
                        inTransit: typeInTransit,
                        outOfService: typeOutOfService,
                        retired: typeRetired,
                        lost: typeLost,
                        delivered: typeDelivered, // 该类型下的外放数量
                        isChild: true,
                    };
                });
            }

            // 累加到总计中
            totalSummary.totalCount += totalCount;
            totalSummary.inStock += inStock;
            totalSummary.delivered += delivered;
            totalSummary.inTransit += inTransit;
            totalSummary.outOfService += outOfService;
            totalSummary.retired += retired;
            totalSummary.lost += lost;

            return {
                key: `airport-${index}`,
                airport_iata: airport.airport_iata,
                airport_name: airport.airport_name || airport.airport_iata,
                totalCount,
                // V3核心状态（从ULD列表计算得出）
                inStock,
                inTransit,
                outOfService,
                retired,
                lost,
                delivered, // 外放数量
                typeStats,
                children,
            };
        });

        // 添加总计行（放在数据最后）
        if (tableData.length > 0) {
            tableData.push({
                key: 'summary-total',
                airport_iata: '总计',
                airport_name: '所有机场合计',
                totalCount: totalSummary.totalCount,
                inStock: totalSummary.inStock,
                delivered: totalSummary.delivered,
                inTransit: totalSummary.inTransit,
                outOfService: totalSummary.outOfService,
                retired: totalSummary.retired,
                lost: totalSummary.lost,
                typeStats: {},
                children: [],
                isSummary: true, // 标记为总计行
            });
        }

        return tableData;
    };

    useEffect(() => {
        fetchData();
    }, [debouncedUldIdentifier, selectedAirports, selectedUldTypes, selectedGhas, selectedOwnershipTypes, selectedOwners, selectedHolders, refreshTrigger]);

    // 当筛选条件改变时，通知父组件（用于导出功能）
    useEffect(() => {
        if (onFiltersChange) {
            // 构建与API兼容的筛选参数格式
            const filterParams = {};
            
            if (debouncedUldIdentifier && debouncedUldIdentifier.trim()) {
                filterParams.uld_identifiers = debouncedUldIdentifier.trim();
            }
            
            if (selectedAirports && selectedAirports.length > 0) {
                filterParams.airport_iatas = selectedAirports.join(',');
            }
            
            if (selectedUldTypes && selectedUldTypes.length > 0) {
                filterParams.uld_types = selectedUldTypes.join(',');
            }
            
            if (selectedGhas && selectedGhas.length > 0) {
                filterParams.ghas = selectedGhas.join(',');
            }
            
            if (selectedOwnershipTypes && selectedOwnershipTypes.length > 0) {
                filterParams.ownership_types = selectedOwnershipTypes.join(',');
            }
            
            if (selectedOwners && selectedOwners.length > 0) {
                filterParams.owners = selectedOwners.join(',');
            }
            
            if (selectedHolders && selectedHolders.length > 0) {
                filterParams.holders = selectedHolders.join(',');
            }
            
            onFiltersChange(filterParams);
        }
    }, [debouncedUldIdentifier, selectedAirports, selectedUldTypes, selectedGhas, selectedOwnershipTypes, selectedOwners, selectedHolders, onFiltersChange]);

    // 表格列定义
    const columns = [
        {
            title: (
                <Space>
                    <EnvironmentOutlined />
                    机场站点
                </Space>
            ),
            dataIndex: 'airport_iata',
            key: 'airport_iata',
            width: 150,
            fixed: 'left',
            render: (iata, record) => {
                if (record.isChild) {
                    return (
                        <div style={{ paddingLeft: 24 }}>
                            <Text strong style={{ color: '#1890ff' }}>{record.uldNumber}</Text>
                            <Text type="secondary" style={{ fontSize: '12px', marginLeft: 8 }}>
                                ({record.uldType})
                            </Text>
                        </div>
                    );
                }
                
                return <Text strong>{iata}</Text>;
            },
        },
        {
            title: '总数',
            dataIndex: 'totalCount',
            key: 'totalCount',
            width: 80,
            align: 'center',
            render: (count, record) => {
                if (record.isChild) {
                    return <Text strong>{record.typeCount ?? record.totalCount ?? 1}</Text>;
                }
                return <Statistic value={count} valueStyle={{ fontSize: 16 }} />;
            },
        },
        // V3核心状态列
        {
            title: '在库',
            dataIndex: 'inStock',
            key: 'inStock',
            width: 80,
            align: 'center',
            render: (count, record) => {
                return (
                    <Text style={{ color: ULD_CORE_STATUS_COLORS.IN_STOCK, fontWeight: 'bold' }}>
                        {count}
                    </Text>
                );
            },
        },
        {
            title: '外放',
            dataIndex: 'delivered',
            key: 'delivered',
            width: 80,
            align: 'center',
            render: (count, record) => {
                return (
                    <Text style={{ color: '#fa8c16', fontWeight: 'bold' }}>
                        {count || 0}
                    </Text>
                );
            },
        },
        {
            title: '在途',
            dataIndex: 'inTransit',
            key: 'inTransit',
            width: 80,
            align: 'center',
            render: (count, record) => {
                return (
                    <Text style={{ color: ULD_CORE_STATUS_COLORS.IN_TRANSIT, fontWeight: 'bold' }}>
                        {count}
                    </Text>
                );
            },
        },
        {
            title: '停用',
            dataIndex: 'outOfService',
            key: 'outOfService',
            width: 80,
            align: 'center',
            render: (count, record) => {
                return (
                    <Text style={{ color: ULD_CORE_STATUS_COLORS.OUT_OF_SERVICE, fontWeight: 'bold' }}>
                        {count}
                    </Text>
                );
            },
        },
        {
            title: '退役',
            dataIndex: 'retired',
            key: 'retired',
            width: 80,
            align: 'center',
            render: (count, record) => {
                return (
                    <Text style={{ color: ULD_CORE_STATUS_COLORS.RETIRED, fontWeight: 'bold' }}>
                        {count}
                    </Text>
                );
            },
        },
        {
            title: '丢失',
            dataIndex: 'lost',
            key: 'lost',
            width: 80,
            align: 'center',
            render: (count, record) => {
                return (
                    <Text style={{ color: ULD_CORE_STATUS_COLORS.LOST, fontWeight: 'bold' }}>
                        {count || 0}
                    </Text>
                );
            },
        },
    ];

    // 自定义展开图标
    const expandIcon = ({ expanded, onExpand, record }) => {
        if (record.isChild || record.isSummary || !record.children || record.children.length === 0) {
            return null;
        }
        
        return (
            <span 
                onClick={e => onExpand(record, e)}
                style={{ cursor: 'pointer', marginRight: 8 }}
            >
                {expanded ? <DownOutlined /> : <RightOutlined />}
            </span>
        );
    };

    // 处理展开变化
    const handleExpand = (expanded, record) => {
        if (expanded) {
            setExpandedRowKeys([...expandedRowKeys, record.key]);
        } else {
            setExpandedRowKeys(expandedRowKeys.filter(key => key !== record.key));
        }
    };

    if (error) {
        return (
            <Card>
                <Alert 
                    message="数据加载失败" 
                    description={error} 
                    type="error" 
                    showIcon 
                />
            </Card>
        );
    }

    return (
        <div>
            {/* 筛选器 - 第一行：ULD识别码、机场、ULD类型 */}
            <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={6}>
                    <Input
                        placeholder="输入ULD识别码（模糊匹配）"
                        value={uldIdentifier}
                        onChange={(e) => setUldIdentifier(e.target.value)}
                        allowClear
                        style={{ width: '100%' }}
                    />
                </Col>
                <Col span={6}>
                    <Select
                        mode="multiple"
                        placeholder={airportsLoading ? "加载中..." : (airportsError ? "加载失败" : "选择机场")}
                        value={selectedAirports}
                        onChange={setSelectedAirports}
                        allowClear
                        maxTagCount={2}
                        loading={airportsLoading}
                        showSearch
                        optionFilterProp="label"
                        filterOption={(input, option) => {
                            if (!input) return true;
                            const iataCode = (option?.label ?? "").toLowerCase();
                            const inputLower = input.toLowerCase();
                            return iataCode.includes(inputLower);
                        }}
                        notFoundContent={
                            airportsLoading ? "加载中..." : 
                            airportsError ? `加载失败: ${airportsError}` :
                            airportOptions.length === 0 ? "暂无机场数据" : "无匹配的机场代码"
                        }
                        dropdownStyle={{ maxHeight: 300, overflow: 'auto' }}
                        style={{ width: '100%' }}
                    >
                        {airportOptions.map(option => (
                            <Option key={option.value} value={option.value} label={option.label}>
                                {option.label}
                            </Option>
                        ))}
                    </Select>
                </Col>
                <Col span={6}>
                    <Select
                        mode="multiple"
                        placeholder="选择ULD类型"
                        value={selectedUldTypes}
                        onChange={setSelectedUldTypes}
                        allowClear
                        maxTagCount={2}
                        showSearch
                        optionFilterProp="value"
                        filterOption={(input, option) => {
                            if (!input) return true;
                            const typeValue = (option?.value ?? "").toLowerCase();
                            const inputLower = input.toLowerCase();
                            return typeValue.includes(inputLower);
                        }}
                        dropdownStyle={{ maxHeight: 300, overflow: 'auto' }}
                        style={{ width: '100%' }}
                    >
                        {ULD_TYPES.map(type => (
                            <Option key={type} value={type}>
                                {type}
                            </Option>
                        ))}
                    </Select>
                </Col>
                <Col span={6}>
                    <Select
                        mode="multiple"
                        placeholder="选择地面处理代理"
                        value={selectedGhas}
                        onChange={setSelectedGhas}
                        allowClear
                        maxTagCount={2}
                        loading={optionsLoading}
                        showSearch
                        optionFilterProp="value"
                        filterOption={(input, option) => {
                            if (!input) return true;
                            const ghaValue = (option?.value ?? "").toLowerCase();
                            const inputLower = input.toLowerCase();
                            return ghaValue.includes(inputLower);
                        }}
                        notFoundContent={
                            optionsLoading ? "加载中..." :
                            filterOptions.ghas.length === 0 ? "暂无地面处理代理数据" : "无匹配的地面处理代理"
                        }
                        dropdownStyle={{ maxHeight: 300, overflow: 'auto' }}
                        style={{ width: '100%' }}
                    >
                        {filterOptions.ghas.map(gha => (
                            <Option key={gha} value={gha}>
                                {gha}
                            </Option>
                        ))}
                    </Select>
                </Col>
            </Row>

            {/* 筛选器 - 第二行：所有权类型、所有者、持有者 */}
            <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={6}>
                    <Select
                        mode="multiple"
                        placeholder="选择所有权类型"
                        value={selectedOwnershipTypes}
                        onChange={setSelectedOwnershipTypes}
                        allowClear
                        maxTagCount={2}
                        showSearch
                        optionFilterProp="value"
                        filterOption={(input, option) => {
                            if (!input) return true;
                            const ownershipValue = (option?.value ?? "").toLowerCase();
                            const inputLower = input.toLowerCase();
                            return ownershipValue.includes(inputLower);
                        }}
                        dropdownStyle={{ maxHeight: 300, overflow: 'auto' }}
                        style={{ width: '100%' }}
                    >
                        {ULD_OWNERSHIP_TYPE_OPTIONS.map(option => (
                            <Option key={option.value} value={option.value}>
                                {option.label}
                            </Option>
                        ))}
                    </Select>
                </Col>
                <Col span={6}>
                    <Select
                        mode="multiple"
                        placeholder="选择所有者"
                        value={selectedOwners}
                        onChange={setSelectedOwners}
                        allowClear
                        maxTagCount={2}
                        loading={optionsLoading}
                        showSearch
                        optionFilterProp="value"
                        filterOption={(input, option) => {
                            if (!input) return true;
                            const ownerValue = (option?.value ?? "").toLowerCase();
                            const inputLower = input.toLowerCase();
                            return ownerValue.includes(inputLower);
                        }}
                        notFoundContent={
                            optionsLoading ? "加载中..." :
                            filterOptions.owners.length === 0 ? "暂无所有者数据" : "无匹配的所有者"
                        }
                        dropdownStyle={{ maxHeight: 300, overflow: 'auto' }}
                        style={{ width: '100%' }}
                    >
                        {filterOptions.owners.map(owner => (
                            <Option key={owner} value={owner}>
                                {owner}
                            </Option>
                        ))}
                    </Select>
                </Col>
                <Col span={6}>
                    <Select
                        mode="multiple"
                        placeholder="选择持有者"
                        value={selectedHolders}
                        onChange={setSelectedHolders}
                        allowClear
                        maxTagCount={2}
                        loading={optionsLoading}
                        showSearch
                        optionFilterProp="value"
                        filterOption={(input, option) => {
                            if (!input) return true;
                            const holderValue = (option?.value ?? "").toLowerCase();
                            const inputLower = input.toLowerCase();
                            return holderValue.includes(inputLower);
                        }}
                        notFoundContent={
                            optionsLoading ? "加载中..." :
                            filterOptions.holders.length === 0 ? "暂无持有者数据" : "无匹配的持有者"
                        }
                        dropdownStyle={{ maxHeight: 300, overflow: 'auto' }}
                        style={{ width: '100%' }}
                    >
                        {filterOptions.holders.map(holder => (
                            <Option key={holder} value={holder}>
                                {holder}
                            </Option>
                        ))}
                    </Select>
                </Col>
                <Col span={6}>
                    {/* 预留空间 */}
                </Col>
            </Row>

            <Table
                columns={columns}
                dataSource={data}
                loading={loading}
                size="small"
                bordered
                pagination={{
                    pageSize: 20,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total) => {
                        // 总计行不计入站点统计
                        const siteCount = data.some(item => item.isSummary) ? total - 1 : total;
                        return `共 ${siteCount} 个站点${data.some(item => item.isSummary) ? ' + 1行总计' : ''}`;
                    },
                }}
                scroll={{ x: 800 }}
                expandable={{
                    expandIcon,
                    expandedRowKeys,
                    onExpand: handleExpand,
                    expandRowByClick: true,
                    childrenColumnName: 'children',
                }}
                rowClassName={(record) => {
                    if (record.isChild) {
                        return 'child-row';
                    }
                    if (record.isSummary) {
                        return 'summary-row';
                    }
                    return '';
                }}
            />

            <style>{`
                .child-row {
                    background-color: #fafafa !important;
                }
                .child-row:hover {
                    background-color: #f0f0f0 !important;
                }
                .summary-row {
                    background-color: #e6f7ff !important;
                    font-weight: bold !important;
                    border-top: 2px solid #1890ff !important;
                }
                .summary-row:hover {
                    background-color: #bae7ff !important;
                }
                .summary-row td {
                    font-weight: bold !important;
                    font-size: 14px !important;
                }
            `}</style>
        </div>
    );
};

export default UldLocationStatTable;